import { Fragment, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useImmer } from 'use-immer'
import { Box, Button, Card, CardBody, CardHeader, Checkbox, Flex, FormControl, FormLabel, Grid, GridItem, Heading, Input, Text } from '@chakra-ui/react'
import { UiBreadcrumb } from 'components/common'
import { useAppContext } from 'hooks/useAppContext'
import { getConfigEmcPricing, updateConfigByName } from 'api'
import { APP_CONFIG_FIELDS, EMC_PITCHES } from 'lib'

const BreadcrumbNav = [
  {
    label: 'Display Pricing Config',
    isCurrentPage: true
  }
]

const DEFAULT_POWER_ROWS = [
  { pitch: '15mm', watts: { typical: 11, max: 36 } },
  { pitch: '9mm', watts: { typical: 20, max: 65 } },
  { pitch: '6mm', watts: { typical: 17, max: 59 } },
  { pitch: '4mm', watts: { typical: 17, max: 80 } }
]

const ensurePowerRows = (rows = []) => {
  return Object.values(EMC_PITCHES).map(pitch => {
    const existing = rows.find(row => row.pitch === pitch)
    return {
      pitch,
      watts: {
        typical: existing?.watts?.typical ?? 0,
        max: existing?.watts?.max ?? 0
      }
    }
  })
}

const serializePowerRows = (rows = []) => {
  return ensurePowerRows(rows).map(row => ({
    pitch: row.pitch,
    watts: {
      typical: parseFloat(row.watts.typical) || 0,
      max: parseFloat(row.watts.max) || 0
    }
  }))
}

const ElectricalWattFields = ({ modelKey, powers, setForm }) => {
  const updateWatt = (pitch, field, value) => {
    setForm(draft => {
      const row = draft.powers[modelKey].find(item => item.pitch === pitch)
      if (row) row.watts[field] = value
    })
  }

  return (
    <>
      <GridItem colSpan={2}>
        <Heading size="xs" mt={6}>Electrical Watts</Heading>
        <Text fontSize="sm" color="gray.600" mt={1}>
          Quote PDFs calculate 120v and 240v amps from max watts. Typical amps are 35% of that max-amp figure.
        </Text>
      </GridItem>
      {Object.values(EMC_PITCHES).map(pitch => {
        const watts = powers.find(row => row.pitch === pitch)?.watts || { typical: 0, max: 0 }
        return (
          <Fragment key={pitch}>
            <GridItem>
              <FormControl isRequired>
                <FormLabel>{pitch} Typical Watts</FormLabel>
                <Input
                  type="number"
                  value={watts.typical}
                  onChange={(e) => updateWatt(pitch, 'typical', e.target.value)}
                />
              </FormControl>
            </GridItem>
            <GridItem>
              <FormControl isRequired>
                <FormLabel>{pitch} Max Watts</FormLabel>
                <Input
                  type="number"
                  value={watts.max}
                  onChange={(e) => updateWatt(pitch, 'max', e.target.value)}
                />
              </FormControl>
            </GridItem>
          </Fragment>
        )
      })}
    </>
  )
}

ElectricalWattFields.propTypes = {
  modelKey: PropTypes.oneOf(['g4', 'g5']).isRequired,
  powers: PropTypes.arrayOf(PropTypes.shape({
    pitch: PropTypes.string,
    watts: PropTypes.shape({
      typical: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      max: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    })
  })).isRequired,
  setForm: PropTypes.func.isRequired
}

export const DisplayPricingsConfig = () => {
  const { createToast } = useAppContext()
  const [form, setForm] = useImmer({
    markup: { g4: 0, g5: 0 },
    ppms: { g4: [], g5: [] },
    modelVisibility: { g4: true, g5: true },
    powers: {
      g4: ensurePowerRows(DEFAULT_POWER_ROWS),
      g5: ensurePowerRows(DEFAULT_POWER_ROWS)
    }
  })
  const [isSaving, setIsSaving] = useState(false)

  const load = async () => {
    try {
      const res = await getConfigEmcPricing()
      const data = res.data || res
      setForm(draft => {
        draft.markup = data.markup || draft.markup
        draft.ppms = data.ppms || draft.ppms
        draft.modelVisibility = data.modelVisibility || { g4: true, g5: true }
        draft.powers = {
          g4: ensurePowerRows(data.powers?.g4 || DEFAULT_POWER_ROWS),
          g5: ensurePowerRows(data.powers?.g5 || DEFAULT_POWER_ROWS)
        }
      })
    } catch (err) {
      createToast('error', 'Error', err.response?.data ?? err.message)
    }
  }

  useEffect(() => {
    load()
  }, [])

  const handleSubmit = async (e) => {
    e.preventDefault()

    setIsSaving(true)
    try {
      await updateConfigByName(APP_CONFIG_FIELDS.DisplayPricingMarkup, {
        value: JSON.stringify({
          g4: parseFloat(form.markup.g4),
          g5: parseFloat(form.markup.g5)
        })
      })
      await updateConfigByName(APP_CONFIG_FIELDS.DisplayPricingPPMs, {
        value: JSON.stringify({
          g4: form.ppms.g4.map(ppm => ({ ...ppm, price: parseFloat(ppm.price) })),
          g5: form.ppms.g5.map(ppm => ({ ...ppm, price: parseFloat(ppm.price) }))
        })
      })
      await updateConfigByName(APP_CONFIG_FIELDS.EmcModelVisibility, {
        value: JSON.stringify(form.modelVisibility)
      })
      await updateConfigByName(APP_CONFIG_FIELDS.DisplayPricingPowers, {
        value: JSON.stringify({
          g4: serializePowerRows(form.powers.g4),
          g5: serializePowerRows(form.powers.g5)
        })
      })
      load()
      createToast('success', 'Successfully updated.')
      setIsSaving(false)
    } catch (err) {
      createToast('error', 'Error', err.response.data)
      setIsSaving(false)
    }
  }

  return (
    <>
      <UiBreadcrumb items={BreadcrumbNav} />
      <Box>
        <Flex justifyContent="space-between" alignItems="center" my={8}>
          <Heading size="lg">Display Pricing Config</Heading>
        </Flex>
        <Card borderRadius={0}>
          <CardHeader bg="gray.500" p={4} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
            <Heading color="white" size="sm">G4</Heading>
            <Checkbox
              colorScheme="whiteAlpha"
              color="white"
              isChecked={form.modelVisibility.g4}
              onChange={(e) => setForm(draft => { draft.modelVisibility.g4 = e.target.checked })}
            >
              Show in Edge Quote
            </Checkbox>
          </CardHeader>
          <CardBody p={8}>
            <form onSubmit={handleSubmit}>
              <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                <GridItem>
                  <FormControl isRequired>
                    <FormLabel>Markup</FormLabel>
                    <Input type="number" name="markup" value={form.markup.g4} onChange={(e) => { setForm(draft => { draft.markup = { ...form.markup, g4: e.target.value } }) }} />
                  </FormControl>
                </GridItem>
                <GridItem></GridItem>
                {Object.values(EMC_PITCHES).map(v => {
                  return (
                    <GridItem key={v}>
                      <FormControl isRequired>
                        <FormLabel>{v} PPM</FormLabel>
                        <Input
                          type="number"
                          name={v}
                          value={form.ppms.g4.filter(ppm => ppm.pitch === v)?.[0]?.price || 0}
                          onChange={(e) => {
                            setForm(draft => {
                              draft.ppms = { ...form.ppms, g4: form.ppms.g4.map(ppm => ppm.pitch === v ? { ...ppm, price: e.target.value } : ppm) }
                            })
                          }}
                        />
                      </FormControl>
                    </GridItem>
                  )
                })}
                <GridItem></GridItem>
                <ElectricalWattFields modelKey="g4" powers={form.powers.g4} setForm={setForm} />
              </Grid>
              <Button type="submit" mt={8} colorScheme="blue" isLoading={isSaving}>Save</Button>
            </form>
          </CardBody>
        </Card>
        <Card borderRadius={0} mt={8}>
          <CardHeader bg="gray.500" p={4} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
            <Heading color="white" size="sm">G5</Heading>
            <Checkbox
              colorScheme="whiteAlpha"
              color="white"
              isChecked={form.modelVisibility.g5}
              onChange={(e) => setForm(draft => { draft.modelVisibility.g5 = e.target.checked })}
            >
              Show in Edge Quote
            </Checkbox>
          </CardHeader>
          <CardBody p={8}>
            <form onSubmit={handleSubmit}>
              <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                <GridItem>
                  <FormControl isRequired>
                    <FormLabel>Markup</FormLabel>
                    <Input type="number" name="markup" value={form.markup.g5} onChange={(e) => { setForm(draft => { draft.markup = { ...form.markup, g5: e.target.value } }) }} />
                  </FormControl>
                </GridItem>
                <GridItem></GridItem>
                {Object.values(EMC_PITCHES).map(v => {
                  return (
                    <GridItem key={v}>
                      <FormControl isRequired>
                        <FormLabel>{v} PPM</FormLabel>
                        <Input
                          type="number"
                          name={v}
                          value={form.ppms.g5.filter(ppm => ppm.pitch === v)?.[0]?.price || 0}
                          onChange={(e) => {
                            setForm(draft => {
                              draft.ppms = { ...form.ppms, g5: form.ppms.g5.map(ppm => ppm.pitch === v ? { ...ppm, price: e.target.value } : ppm) }
                            })
                          }}
                        />
                      </FormControl>
                    </GridItem>
                  )
                })}
                <GridItem></GridItem>
                <ElectricalWattFields modelKey="g5" powers={form.powers.g5} setForm={setForm} />
              </Grid>
              <Button type="submit" mt={8} colorScheme="blue" isLoading={isSaving}>Save</Button>
            </form>
          </CardBody>
        </Card>
      </Box>
    </>
  )
}
